/**
 * @file add-sales-representative-modal.tsx
 * @module app/modals/add-sales-representative-modal
 * @description Renders the add sales representative modal modal dialog for confirming actions, capturing workflow input, and reporting outcomes to its parent screen.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import React, { useEffect, useState } from "react";
import { Button, Form, FormLabel, InputGroup, Modal } from "react-bootstrap";
import CrossSvg from "../../_admin/assets/media/svg_uTrade/cross-rounded-gray.svg";
import APICallService from "../../api/apiCallService";
import { assistAdmin } from "../../api/apiEndPoints";
import { assistAdminJson } from "../../api/apiJSON/sales-rep";
import { success } from "../../Global/toast";
import { useAuth } from "../modules/auth";
import EditSVG from "../../_admin/assets/media/svg_uTrade/edit-round.svg";
import { fileValidation } from "../../Global/fileValidation";
import { Manager } from "../../utils/constants";
import uploadIcon from "../../_admin/assets/media/svg_uTrade/upload-img-white.svg";
import ImageWithFallback from "../modules/_common/components/image-with-fallback";

interface AddSalesRepresentativeModalProps {
    show: boolean;
    onHide: () => void;
    onSave: () => void;
    editData?: any;
}

const AddSalesRepresentativeModal: React.FC<AddSalesRepresentativeModalProps> = ({
    show,
    onHide,
    onSave,
    editData,
}) => {
    type FormFields = 'name' | 'email' | 'phone' | 'image' | 'imageReader';
    type FormState = {
        name: string;
        email: string;
        phone: string;
        image: string;
        imageReader: any;
    };
    type ValidationState = {
        name: boolean;
        email: boolean;
        phone: boolean;
        image: boolean;
    };

    const { currentUser } = useAuth();

    const [form, setForm] = useState<FormState>({ name: "", email: "", phone: "", image: "", imageReader: "" });
    const [loading, setLoading] = useState(false);
    const [validation, setValidation] = useState<ValidationState>({ name: false, email: false, phone: false, image: false });

    useEffect(() => {
        if (show) {
            if (editData) {
                setForm({
                    name: editData.name || "",
                    email: editData.email || "",
                    phone: editData.phone || "",
                    image: editData.image || "",
                    imageReader: "",
                });
            } else {
                setForm({ name: "", email: "", phone: "", image: "", imageReader: "" });
            }
            setValidation({ name: false, email: false, phone: false, image: false });
            setLoading(false);
        }
    }, [show, editData]);

    const handleChange = (field: keyof FormState, value: any) => {
        setForm((prev) => ({ ...prev, [field]: value }));
        if (validation[field as keyof ValidationState] && (typeof value === 'string' ? value.trim() !== "" : !!value)) {
            setValidation((prev) => ({ ...prev, [field]: false }));
        }
    };

    const handleImageInput = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = event.target.files;
        let temp = { ...form };
        let tempValidation: any = { ...validation };
        tempValidation.image = true;
        if (!selectedFiles || selectedFiles.length === 0) return;
        else {
            if (fileValidation(selectedFiles[0])) {
                temp.image = URL.createObjectURL(selectedFiles[0]);
                temp.imageReader = selectedFiles[0];
                tempValidation.image = false;
            }
        }
        setValidation(tempValidation);
        setForm(temp);
    };

    const handleSave = async () => {
        const tmpValidation: ValidationState = {
            name: !form.name.trim(),
            email: !form.email.trim(),
            phone: !form.phone.trim(),
            image: validation.image,
        };
        setValidation(tmpValidation);

        const isValid = Object.values(tmpValidation).every((v) => !v);
        if (!isValid) return;

        setLoading(true);
        let apiService;
         const salesRepData = {
            name: form.name,
            email: form.email,
            phoneCountry: "+255",
            phone: form.phone,
            type: currentUser?.role.some((obj: any) => obj.type === Manager) ? 2 : 7,
            manager: currentUser?._id,
            image: form.imageReader
        };

        if (editData) {
          apiService = new APICallService(
            assistAdmin.edit,
            assistAdminJson.editAssistAdmin(salesRepData),
            { id: editData._id},
            "",
            "",
            "",
            "",
            ""
          );
        } else {
          apiService = new APICallService(
            assistAdmin.add,
            assistAdminJson.addAssistAdmin(salesRepData),
            "",
            "",
            "",
            "",
            "",
            ""
          );
        }

        const response = await apiService.callAPI();
        if (response) {
            success(response.message || `Sales Representative ${editData ? "updated" : "added"} successfully!`);
            onSave();
            onHide();
        }
        setLoading(false);
    };

    return (
        <Modal
            show={show}
            onHide={onHide}
            centered
            dialogClassName="modal-md"
            contentClassName="rounded-15px border-0 shadow-sm"
        >
            <Modal.Header className="border-0 pb-0 position-relative d-flex justify-content-center pt-8">
                <img
                    className="close-inner-top position-absolute start-0 ms-5"
                    width={40}
                    height={40}
                    src={CrossSvg}
                    alt="closebutton"
                    onClick={onHide}
                    style={{ top: "25px", cursor: "pointer" }}
                />
                <Modal.Title className="fw-bolder fs-20 text-dark">{editData ? "Edit" : "Add New"} {currentUser?.role.some((obj: any) => obj.type === Manager) ? "Sales" : "Activation"} Representative!</Modal.Title>
            </Modal.Header>
            <Modal.Body className="px-10 pb-10 pt-4">
                <div className="d-flex justify-content-center mb-7">
                    <label
                        className="image-input image-input-outline rounded-circle bg-white position-relative shadow-sm"
                        style={{ width: "125px", height: "125px", cursor: "pointer" }}
                    >
                        <div className="image-input-wrapper w-100 h-100 rounded-circle overflow-hidden position-relative">
                            {form.image ? (
                                <ImageWithFallback
                                    src={form.image}
                                    alt="Avatar"
                                    className="w-100 h-100 object-fit-cover"
                                    noDefaultSize
                                />
                            ) : (
                                <div className="w-100 h-100 bg-light d-flex align-items-center justify-content-center">
                                    <img
                                        width={168}
                                        height={168}
                                        src={uploadIcon}
                                        className="img-fluid border-r10px"
                                        alt=""
                                    />
                                </div>
                            )}
                        </div>
                        {!loading && (
                            <>
                                <input
                                    type="file"
                                    name="avatar"
                                    accept=".png, .jpg, .jpeg"
                                    onChange={handleImageInput}
                                    className="d-none"
                                />
                                {form.image ? (
                                    <div
                                        className="btn btn-icon btn-circle btn-active-color-primary w-35px h-35px bg-white shadow position-absolute d-flex align-items-center justify-content-center"
                                        style={{ bottom: "0px", right: "0px", zIndex: 1 }}
                                    >
                                        <img src={EditSVG} alt="" height={20} width={20} />
                                    </div>
                                ) : (
                                    <></>
                                )}
                            </>
                        )}
                    </label>
                </div>
                <Form className="mt-4">
                    <div className="mb-5">
                        <FormLabel className="fs-16 fw-500 mb-3 text-dark">Rep Name</FormLabel>
                        <Form.Control
                            type="text"
                            className={`form-control-custom bg-white ${validation.name ? "border-danger" : ""}`}
                            placeholder="Type here"
                            value={form.name}
                            onChange={(e) => handleChange('name', e.target.value)}
                        />
                    </div>
                    <div className="mb-5">
                        <FormLabel className="fs-16 fw-500 mb-3 text-dark">Email</FormLabel>
                        <Form.Control
                            type="text"
                            className={`form-control-custom bg-white ${validation.email ? "border-danger" : ""}`}
                            placeholder="Type here"
                            value={form.email}
                            onChange={(e) => handleChange('email', e.target.value)}
                        />
                    </div>
                    <div className="mb-7">
                        <FormLabel className="fs-16 fw-500 mb-3 text-dark">Phone</FormLabel>
                        <InputGroup>
                            <InputGroup.Text
                                className={`bg-white border-right-0 fs-16 fw-600 text-black px-6 ${validation.phone ? "border-danger" : ""}`}
                            >
                                +255
                            </InputGroup.Text>
                            <Form.Control
                                className={`form-control-custom border-active-none bg-white border-left-0 ps-0 form-control-phone-input ${validation.phone ? "border-danger" : ""}`}
                                name="phone"
                                placeholder="Type here"
                                value={form.phone}
                                onChange={(e) => handleChange('phone', e.target.value)}
                            />
                        </InputGroup>
                    </div>

                    <Button variant="primary" className="uw-btn uw-btn-primary uw-btn-lg w-100 btn-lg shadow-sm" onClick={handleSave} disabled={loading}>
                        {!loading ? (
                            <span className="fs-16 fw-bold">Save</span>
                        ) : (
                            <div className="d-flex justify-content-center">
                                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                            </div>
                        )}
                    </Button>
                </Form>
            </Modal.Body>
        </Modal>
    );
};

export default AddSalesRepresentativeModal;
